/** Sanitize and persist first-touch UTM fields only. */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
export type UtmKey = (typeof UTM_KEYS)[number]
export type UtmBag = Partial<Record<UtmKey, string>>

const STORAGE_KEY = 'pp_utm_attrs'
const MAX_LEN = 64
const SAFE = /^[a-zA-Z0-9._%-]+$/

function sanitizeValue(raw: string | null): string | undefined {
  if (!raw) return undefined
  const trimmed = raw.trim().slice(0, MAX_LEN)
  if (!trimmed || !SAFE.test(trimmed)) return undefined
  return trimmed
}

export function readUtmsFromSearch(search: string): UtmBag {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const out: UtmBag = {}
  for (const key of UTM_KEYS) {
    const v = sanitizeValue(params.get(key))
    if (v) out[key] = v
  }
  return out
}

export function loadPersistedUtms(): UtmBag {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: UtmBag = {}
    for (const key of UTM_KEYS) {
      const v = sanitizeValue(typeof parsed[key] === 'string' ? parsed[key] : null)
      if (v) out[key] = v
    }
    return out
  } catch {
    return {}
  }
}

/** Capture current UTMs (if any) and merge into first-touch persistence. */
export function captureAndPersistUtms(search = typeof window !== 'undefined' ? window.location.search : ''): UtmBag {
  const incoming = readUtmsFromSearch(search)
  const existing = loadPersistedUtms()
  // First-touch: only fill missing keys
  const merged: UtmBag = { ...existing }
  for (const key of UTM_KEYS) {
    if (!merged[key] && incoming[key]) merged[key] = incoming[key]
  }
  if (typeof window !== 'undefined' && Object.keys(incoming).length > 0) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
    } catch {
      /* ignore */
    }
  }
  return merged
}

export function clearPersistedUtms(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}
